/* eslint-disable no-secrets/no-secrets */
import { useCallback } from 'react';
import _ from 'lodash';

function isFieldEmpty(field) {
    return _.isUndefined(field) || _.isEmpty(field);
}

function useContactPhoneUtil(jobVM) {

    const getAccountType = useCallback(() => _.get(jobVM, 'baseData.accountHolder.subtype.value'), [jobVM]);

    const getHomePhone = useCallback(() => _.get(jobVM, 'baseData.accountHolder.homeNumber.value'), [jobVM]);

    const getMobilePhone = useCallback(() => _.get(jobVM, 'baseData.accountHolder.cellNumber.value'), [jobVM]);

    const getWorkPhone = useCallback(() => _.get(jobVM, 'baseData.accountHolder.workNumber.value'), [jobVM]);

    const getOfficePhone = useCallback(() => _.get(jobVM, 'baseData.accountHolder.workNumber.value'), [jobVM]);

    const setPrimaryPhoneType = useCallback((value) => {
        if (!isFieldEmpty(getMobilePhone())) {
            // eslint-disable-next-line no-param-reassign
            value = 'mobile';
        }

        _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', value);
    }, [getMobilePhone, jobVM]);

    const setBindDataContactPhone = useCallback((phoneVal) => {
        _.set(jobVM, 'bindData.contactPhone.value', phoneVal);
    }, [jobVM]);

    const setHomePhone = useCallback(() => {
        const homePhone = getHomePhone();

        _.set(jobVM, 'baseData.accountHolder.homeNumber.value', homePhone);

        const code = {
            value: 'home'
        };

        _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.code', code);
        setBindDataContactPhone(homePhone);
    }, [getHomePhone, jobVM, setBindDataContactPhone]);

    const setMobilePhone = useCallback(() => {
        const mobilePhone = getMobilePhone();

        _.set(jobVM, 'baseData.accountHolder.cellNumber.value', mobilePhone);

        const code = {
            value: 'mobile'
        };

        _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.code', code);
        setBindDataContactPhone(mobilePhone);
    }, [getMobilePhone, jobVM, setBindDataContactPhone]);

    const setWorkPhone = useCallback(() => {
        const workPhone = getWorkPhone();

        _.set(jobVM, 'baseData.accountHolder.workNumber.value', workPhone);

        if(_.get(jobVM, 'baseData.accountHolder.primaryPhoneType.value.code') === 'work') {
            setBindDataContactPhone(workPhone);
        }
    }, [getWorkPhone, jobVM, setBindDataContactPhone]);

    const setOfficePhone = useCallback(() => {
        const officePhone = getOfficePhone();

        _.set(jobVM, 'baseData.accountHolder.workNumber.value', officePhone);

        const code = {
            value: 'work'
        };

        _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.code', code);
        setBindDataContactPhone(officePhone);
    }, [getOfficePhone, jobVM, setBindDataContactPhone]);

    const updatePhoneValues = useCallback(() => {
        const accountType = getAccountType();

        // Check if Home and mobile are entered then set home contact as Primary
        const primaryPhoneType = _.get(jobVM, 'baseData.accountHolder.primaryPhoneType.value.code');

        if(getAccountType() === 'Person' && !isFieldEmpty(getMobilePhone()) && !isFieldEmpty(getHomePhone())){
            _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', 'home');
        }

        // Remove primary phone type id the contact number is removed
        if(_.get(jobVM, 'baseData.accountHolder.primaryPhoneType.value.code')){
            if(primaryPhoneType === 'home' && isFieldEmpty(getHomePhone())){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', undefined);
            }

            if(primaryPhoneType === 'work' && (isFieldEmpty(getWorkPhone()) || isFieldEmpty(getOfficePhone()))){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', undefined);
            }

            if(primaryPhoneType === 'mobile' && isFieldEmpty(getMobilePhone())){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', undefined);
            }

        }

        // Set Primary phone type to the type of contact number entered. 
        if(!_.get(jobVM, 'baseData.accountHolder.primaryPhoneType.value.code')){

            if(!isFieldEmpty(getHomePhone())){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', 'home');
            }
            else if(!isFieldEmpty(getOfficePhone()) || !isFieldEmpty(getWorkPhone())){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', 'work');
            }else if(!isFieldEmpty(getMobilePhone())){
                _.set(jobVM, 'baseData.accountHolder.primaryPhoneType.value', 'mobile');
            }
        }
        

        if (accountType === 'Person' && !_.isUndefined(getHomePhone())) {
            setHomePhone();
        }

        if (accountType === 'Person' && !_.isUndefined(getMobilePhone())) {
            setMobilePhone();
        }

        if (accountType === 'Person' && !_.isUndefined(getWorkPhone())) {
            setWorkPhone();
        }

        if (accountType === 'Company' && !_.isUndefined(getOfficePhone())) {
            setOfficePhone();
        }
    }, [getAccountType, jobVM, getMobilePhone, getHomePhone, getWorkPhone, getOfficePhone, setHomePhone, setMobilePhone, setWorkPhone, setOfficePhone]);

    const checkForMobileOrHomePhone = useCallback(() => {
        updatePhoneValues();

        if (isFieldEmpty(getHomePhone())) {
            setPrimaryPhoneType(null);
        }

        return getAccountType() === 'Person' && (isFieldEmpty(getHomePhone()) && isFieldEmpty(getMobilePhone())&& isFieldEmpty(getWorkPhone()));
    }, [getAccountType, getHomePhone, getMobilePhone,getWorkPhone, setPrimaryPhoneType, updatePhoneValues]);

    function checkForOfficePhone() {
        return getAccountType() === 'Company' && isFieldEmpty(getOfficePhone());
    }

    function checkForPhoneValidation() {
        return checkForMobileOrHomePhone() || checkForOfficePhone();
    }

    const checkForMobileOrHomePhoneForContact = useCallback(() => {
        // Check Secondary Named Insured
        const sniVM = _.get(jobVM, 'baseData.secondaryNamedInsured_Ext.value', {});

        if (sniVM.contactType !== 'company' && !_.isUndefined(sniVM.person) && ( isFieldEmpty(sniVM.person.homeNumber) && isFieldEmpty(sniVM.person.cellNumber) && isFieldEmpty(sniVM.person.workNumber))){
            return true; // yes disable Next because both fields are empty
        }

        // Check creditcard
        const ccVM = _.get(jobVM, 'baseData.creditContact_Ext.value', {});

        if(!isFieldEmpty(ccVM)) {
            if (( isFieldEmpty(ccVM.homeNumber) && isFieldEmpty(ccVM.cellNumber) && isFieldEmpty(ccVM.workNumber))){
                return true;
            }
        }   

        // Check AdditionalNamed Insured...
        const anis = _.get(jobVM, 'baseData.additionalNamedInsureds_Ext.value', []);

        return anis.some((ani) => {
            const cType = _.get(ani, 'contactType');

            if (cType === 'person'){
                return isFieldEmpty(ani.person.cellNumber) && isFieldEmpty(ani.person.homeNumber) && isFieldEmpty(ani.person.workNumber);
            }

            return false;
        });

    }, [jobVM]);

    const phoneOverrides = useCallback(() => {
        const accountType = getAccountType();

        return {
            homePhone: {
                visible: accountType === 'Person',
                showRequired: false,
            },
            mobilePhone: {
                visible: accountType === 'Person',
                showRequired: false,
            },
            workPhone: {
                visible: accountType === 'Person',
                showRequired: false,
            },
            officePhone: {
                visible: accountType === 'Company'
            },
            homeOrMobileNeeded: {
                visible: checkForMobileOrHomePhone()
            }
        };
    }, [checkForMobileOrHomePhone, getAccountType]);

    return {
        getAccountType,
        checkForPhoneValidation,
        updatePhoneValues,
        phoneOverrides, 
        checkForMobileOrHomePhoneForContact
    };
}

export default useContactPhoneUtil;